import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Bookmark, MapPin, Calendar, Building2, Clock, X, AlertCircle, Search, Loader2, Sparkles, Check, Share2, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getOpportunities } from "../../services/opportunityService";
import { applyForOpportunity, getMyApplications } from "../../services/applicationService";
import { getCvs, uploadCv } from "../../services/cvService";
import { useAuthContext } from "../../hooks/useAuthContext";

const recommendationTabs = [
  { id: "all", label: "All" },
  { id: "for-you", label: "For You" },
  { id: "friends", label: "Friends Applied" },
  { id: "followed", label: "Followed" },
];

const OpportunitiesFeed = ({ filters, user, onEdit, onDelete, opportunities = [], myApplications = [], setMyApplications, loading = false, userCvs = [], fetchUserCvs }) => {
  const { user: authUser, token, dispatch } = useAuthContext();
  const currentUser = user || authUser;
  const isAdmin = currentUser?.role === 'admin';

  const saved = currentUser?.saved_opportunity_ids || [];

  const [searchQuery, setSearchQuery] = useState("");
  const [activeRecTab, setActiveRecTab] = useState("all");
  const [applyingTo, setApplyingTo] = useState(null);
  const [dismissingOpp, setDismissingOpp] = useState(null);
  const [dismissReason, setDismissReason] = useState("");
  const [dismissedOpps, setDismissedOpps] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('dismissed_opps') || '[]');
    } catch {
      return [];
    }
  });

  const [applyForm, setApplyForm] = useState({ cv: '', message: '', portfolioLink: '', answers: {} });
  const [isApplying, setIsApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  
  // CV State
  const [cvFile, setCvFile] = useState(null);
  const [cvSelectionMode, setCvSelectionMode] = useState('select'); // 'select' or 'upload'

  const processOpportunities = () => {
    let result = opportunities;
    if (activeRecTab === "followed") {
      result = result.filter(o => saved.includes(o.id));
    }
    
    result = result
      .filter(o => !dismissedOpps.includes(o.id))
      .filter(o => !searchQuery || o.title.toLowerCase().includes(searchQuery.toLowerCase()) || o.org.toLowerCase().includes(searchQuery.toLowerCase()))
      .filter(o => {
        if (!filters || Object.keys(filters).length === 0) return true;
        return Object.entries(filters).every(([cat, vals]) => {
          if (!vals || vals.length === 0) return true;
          if (cat === "Type") return vals.includes(o.type);
          if (cat === "Location") return vals.some(v => o.location.toLowerCase().includes(v.toLowerCase()));
          if (cat === "Compensation") {
            const opportunityBadges = Array.isArray(o.badges) ? o.badges : [];
            return vals.some(v => opportunityBadges.some(b => b.toLowerCase() === v.toLowerCase()));
          }
          return true;
        });
      });

    // "For You" Calibration Algorithm
    if (activeRecTab === "for-you" && currentUser) {
      const userMajor = (currentUser.major || "").toLowerCase();
      const userLocation = (currentUser.location || "").toLowerCase();
      const userHobbies = (currentUser.hobbies || []).map(h => h.toLowerCase());
      const userGoals = (currentUser.learning_goals || []).map(g => g.toLowerCase());
      
      result = result.map(opp => {
        let score = 0;
        const oppTitle = opp.title.toLowerCase();
        const oppExcerpt = (opp.excerpt || "").toLowerCase();
        const oppLocation = (opp.location || "").toLowerCase();
        const oppMajors = (opp.majors || []).map(m => m.toLowerCase());
        
        // Heavy weight for Major/Field match
        if (userMajor && (oppMajors.includes(userMajor) || oppMajors.some(m => m.includes(userMajor) || userMajor.includes(m)) || oppTitle.includes(userMajor))) {
          score += 5;
        }
        
        // Medium weight for Location match
        if (userLocation && oppLocation.includes(userLocation)) {
          score += 3;
        }
        
        // Lighter weight for Hobbies & Goals matching in text
        const searchKeywords = [...userHobbies, ...userGoals].filter(kw => kw.length > 2); // Avoid matching tiny generic words
        searchKeywords.forEach(kw => {
           if (oppTitle.includes(kw)) score += 2;
           else if (oppExcerpt.includes(kw)) score += 1;
        });
        
        // Add tiny random fraction for tie-breaking so multiple 0 scores don't always show up in exact same order
        score += Math.random() * 0.1;

        return { ...opp, _matchScore: score };
      }).sort((a, b) => b._matchScore - a._matchScore);
    }

    return result;
  };

  const filtered = processOpportunities();

  useEffect(() => {
    // Set default CV if available
    const defaultCv = userCvs?.find(c => c.isDefault);
    if (defaultCv && !applyForm.cv) {
      setApplyForm(prev => ({ ...prev, cv: defaultCv.url }));
    }
  }, [userCvs]);

  const handleSave = async (id) => {
    const isSaved = saved.includes(id);
    const newSaved = isSaved ? saved.filter(s => s !== id) : [...saved, id];
    
    dispatch({ type: 'UPDATE_USER', payload: { saved_opportunity_ids: newSaved } });
    
    if (!token) return;
    try {
      await fetch('/api/auth/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ saved_opportunity_ids: newSaved })
      });
    } catch (err) { console.error(err); }
  };

  const handleShare = async (opp) => {
    const shareData = {
      title: opp.title,
      text: opp.excerpt || `Check out this ${opp.type} at ${opp.org}!`,
      url: window.location.href + `?opportunityId=${opp.id}`
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        alert("Link copied to clipboard!");
      }
    } catch (err) {
      console.error("Error sharing:", err);
    }
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!applyingTo || !token) return;
    setIsApplying(true);
    setApplyError(null);
    try {
      const cvUrl = null; // No longer required in the new simplified form logic


      const response = await applyForOpportunity({...applyForm, cv: cvUrl, opportunityId: applyingTo.id}, token);
      if (setMyApplications) {
        setMyApplications([response.application, ...myApplications]);
      }
      setApplyingTo(null);
      setApplyForm({ cv: '', message: '', portfolioLink: '', answers: {} });
      setCvFile(null);
      setCvSelectionMode('select');
    } catch (err) { 
      setApplyError(err.message);
      setTimeout(() => setApplyError(null), 4000);
    } finally { 
      setIsApplying(false); 
    }
  };

  return (
    <div className="space-y-5">
      {/* Search & Tabs */}
      <div className="stat-card !p-4 space-y-3">
        <div className="relative">
           <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
           <input
             value={searchQuery}
             onChange={(e) => setSearchQuery(e.target.value)}
             placeholder="Search opportunities by title, company, or type..."
             className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring"
           />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
           {recommendationTabs.map((tab) => (
             <button
               key={tab.id}
               onClick={() => setActiveRecTab(tab.id)}
               className={`flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold transition-colors border cursor-pointer flex items-center gap-2 ${
                 activeRecTab === tab.id 
                   ? "bg-secondary/15 text-secondary border-secondary/30" 
                   : "bg-card text-foreground border-border hover:border-secondary hover:bg-secondary/5"
               }`}
             >
               {tab.id === "for-you" && <Sparkles size={12} className="text-orange-400" fill="currentColor" />}
               {tab.label}
             </button>
           ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-secondary" /></div>
      ) : (
        <div className="space-y-4">
          {filtered.map((opp) => (
            <div key={opp.id} className="stat-card !p-4 group">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-secondary/15 text-secondary">{opp.type}</span>
                </div>
                
                <div className="flex items-center gap-1">
                  <button 
                    onClick={() => handleShare(opp)}
                    className="h-8 w-8 rounded-lg border border-transparent flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer bg-transparent"
                    title="Share Opportunity"
                  >
                    <Share2 size={15} />
                  </button>
                  <button 
                    onClick={() => handleSave(opp.id)}
                    className={`h-8 w-8 rounded-lg border flex items-center justify-center transition-colors cursor-pointer ${saved.includes(opp.id) ? 'bg-secondary/20 border-secondary' : 'border-transparent hover:bg-muted'}`}
                  >
                    <Bookmark size={15} className={saved.includes(opp.id) ? "fill-secondary text-secondary" : "text-muted-foreground"} />
                  </button>
                </div>
              </div>

              <h3 className="text-sm font-display font-bold text-foreground mb-0.5 group-hover:text-secondary transition-colors">{opp.title}</h3>
              <p className="text-xs text-muted-foreground mb-3">{opp.orgFull || opp.org}</p>

              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{opp.excerpt}</p>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground mb-4">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {opp.location}</span>
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Deadline: {opp.deadline}</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {opp.timeCommitment}</span>
              </div>

              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4">
                <Building2 className="w-3 h-3" />
                <span>{opp.orgShort || opp.org}</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-secondary/15 text-secondary font-bold ml-2">Verified Partner</span>
              </div>

              <div className="flex items-center gap-2 mt-2">
                 {myApplications.some(app => String(app.opportunityId) === String(opp.id)) ? (
                   <button disabled className="h-9 px-4 rounded-xl bg-muted text-muted-foreground font-medium text-sm flex items-center gap-2 border-none">
                     <Check size={14} /> APPLIED
                   </button>
                 ) : (
                   <>
                       <button
                        onClick={() => {
                          setApplyingTo(opp);
                        }}
                        className="h-9 px-5 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors cursor-pointer border-none"
                      >
                        Apply Now
                      </button>
                     <button 
                       onClick={() => setDismissingOpp(opp)}
                       className="h-9 px-4 rounded-xl border border-destructive/20 text-xs text-destructive hover:bg-destructive/10 transition-colors cursor-pointer bg-transparent font-medium"
                     >
                      Not Interested
                    </button>
                  </>
                 )}
              </div>
            </div>
          ))}
        </div>
      )}

      {applyingTo && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-6" style={{backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', backgroundColor: 'rgba(0,0,0,0.55)'}}>
           <motion.div 
             initial={{ opacity: 0, y: 40, scale: 0.97 }}
             animate={{ opacity: 1, y: 0, scale: 1 }}
             transition={{ type: 'spring', stiffness: 300, damping: 28 }}
             className="bg-card w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl shadow-[0_32px_80px_rgba(0,0,0,0.4)] border border-border/40 overflow-hidden"
           >
              {/* Clean header with icon badge */}
              <div className="px-7 pt-8 pb-6 border-b border-border/20 flex justify-between items-start gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/></svg>
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.14em] text-primary/60 mb-1.5">Applying for</p>
                    <h3 className="text-lg font-bold text-foreground leading-snug">{applyingTo.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1 font-medium">{applyingTo.org}</p>
                  </div>
                </div>
                <button onClick={() => setApplyingTo(null)} className="p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-all border-none bg-transparent cursor-pointer flex-shrink-0">
                  <X size={18} />
                </button>
              </div>

              <div className="overflow-y-auto max-h-[70vh] sm:max-h-[75vh]">
                <div className="px-7 py-7">
                 <form onSubmit={handleApplySubmit} className="space-y-5">


                  {applyingTo.questions && applyingTo.questions.length > 0 && (
                    <div className="pt-4 space-y-6 border-t border-border">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        <h4 className="text-xs font-black uppercase tracking-widest text-foreground/70">Required Questionnaire</h4>
                      </div>
                      
                      {applyingTo.questions.map((q) => (
                        <div key={q.id} className="space-y-3">
                          <label className="text-sm font-bold text-foreground flex items-center gap-1.5">
                            {q.label} {q.required && <span className="text-rose-500">*</span>}
                          </label>
                          
                          {(q.type === 'text' || q.type === 'short_text') && (
                            <input 
                              required={q.required}
                              className="w-full h-11 px-4 rounded-xl bg-muted/30 border border-[#062a2b]/5 dark:border-white/5 text-sm text-foreground focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                              value={applyForm.answers[q.id] || ''}
                              onChange={(e) => setApplyForm({
                                ...applyForm, 
                                answers: { ...applyForm.answers, [q.id]: e.target.value }
                              })}
                              placeholder="Your answer..."
                            />
                          )}

                          {(q.type === 'textarea' || q.type === 'long_text') && (
                            <textarea 
                              required={q.required}
                              rows={3}
                              className="w-full p-4 rounded-xl bg-muted/30 border border-[#062a2b]/5 dark:border-white/5 text-sm text-foreground focus:ring-2 focus:ring-primary/20 outline-none resize-none transition-all"
                              value={applyForm.answers[q.id] || ''}
                              onChange={(e) => setApplyForm({
                                ...applyForm, 
                                answers: { ...applyForm.answers, [q.id]: e.target.value }
                              })}
                              placeholder="Your detailed answer..."
                            />
                          )}

                          {(q.type === 'radio' || q.type === 'multiple_choice' || q.type === 'dropdown') && (
                            <div className="space-y-2" role="radiogroup">
                              {q.options.map((opt, i) => {
                                const inputId = `radio-${q.id}-${i}`;
                                return (
                                  <div key={inputId} className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${applyForm.answers[q.id] === i ? 'bg-primary/8 border-primary/40' : 'bg-muted/30 border-border/30 hover:border-primary/25'}`} onClick={() => setApplyForm({ ...applyForm, answers: { ...applyForm.answers, [q.id]: i } })}>
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${applyForm.answers[q.id] === i ? 'border-primary' : 'border-muted-foreground/40'}`}>
                                      {applyForm.answers[q.id] === i && <div className="w-2 h-2 rounded-full bg-primary" />}
                                    </div>
                                    <input
                                      type="radio"
                                      id={inputId}
                                      name={`q-${q.id}`}
                                      required={q.required && applyForm.answers[q.id] === undefined}
                                      checked={applyForm.answers[q.id] === i}
                                      onChange={() => setApplyForm({ ...applyForm, answers: { ...applyForm.answers, [q.id]: i } })}
                                      className="sr-only"
                                    />
                                    <label htmlFor={inputId} className="text-xs font-medium text-foreground cursor-pointer select-none flex-1">{opt}</label>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {q.type === 'checkbox' && (
                            <div className="space-y-2">
                              {q.options.map((opt, i) => {
                                const inputId = `chk-${q.id}-${i}`;
                                const selectedIndexes = applyForm.answers[q.id] || [];
                                const isChecked = selectedIndexes.includes(i);
                                return (
                                  <div key={inputId} className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${isChecked ? 'bg-primary/8 border-primary/40' : 'bg-muted/30 border-border/30 hover:border-primary/25'}`}
                                    onClick={() => {
                                      const next = isChecked
                                        ? selectedIndexes.filter(v => v !== i)
                                        : [...selectedIndexes, i];
                                      setApplyForm({ ...applyForm, answers: { ...applyForm.answers, [q.id]: next } });
                                    }}>
                                    <div className={`w-4 h-4 rounded-[5px] border-2 flex items-center justify-center flex-shrink-0 transition-all ${isChecked ? 'border-primary bg-primary' : 'border-muted-foreground/40'}`}>
                                      {isChecked && <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                                    </div>
                                    <input type="checkbox" id={inputId} checked={isChecked} onChange={() => {}} className="sr-only" />
                                    <label htmlFor={inputId} className="text-xs font-medium text-foreground cursor-pointer select-none flex-1">{opt}</label>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {applyError && (
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs font-semibold animate-in fade-in slide-in-from-top-2 duration-300">
                      <AlertCircle size={14} className="flex-shrink-0" />
                      <span>{applyError}</span>
                    </div>
                  )}

                  <div className="pt-2 mt-2 border-t border-border/30 space-y-3">
                    <button 
                      disabled={isApplying}
                      className="w-full py-3.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 active:scale-[0.98] transition-all cursor-pointer border-none flex items-center justify-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-primary/20 group"
                    >
                      {isApplying ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <span>Submit Application</span>
                          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:translate-x-0.5 transition-transform"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                        </>
                      )}
                    </button>
                    <button 
                      type="button"
                      onClick={() => setApplyingTo(null)}
                      className="w-full py-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors cursor-pointer bg-transparent border-none"
                    >
                      Cancel
                    </button>
                  </div>
                 </form>
                </div>
              </div>
           </motion.div>
        </div>
      , document.body)}

      {dismissingOpp && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-6" style={{backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', backgroundColor: 'rgba(0,0,0,0.55)'}}>
           <motion.div 
             initial={{ opacity: 0, y: 40, scale: 0.97 }}
             animate={{ opacity: 1, y: 0, scale: 1 }}
             transition={{ type: 'spring', stiffness: 300, damping: 28 }}
             className="bg-card w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-[0_32px_80px_rgba(0,0,0,0.4)] border border-border/40 overflow-hidden"
           >
              <div className="px-7 pt-7 pb-5 border-b border-border/20 flex justify-between items-start gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-destructive/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertCircle size={18} className="text-destructive" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-foreground leading-snug">Not Interested?</h3>
                    <p className="text-xs text-muted-foreground mt-1 font-medium">Help us improve your recommendations.</p>
                  </div>
                </div>
                <button onClick={() => setDismissingOpp(null)} className="p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-all border-none bg-transparent cursor-pointer flex-shrink-0">
                  <X size={18} />
                </button>
              </div>

              <div className="px-7 py-6 space-y-5">
                <div className="space-y-2">
                  {[
                    "Not relevant to my interests",
                    "Location doesn't match",
                    "Compensation is too low",
                    "Requirements are too high",
                    "I don't like this organization",
                    "Other"
                  ].map((reason) => (
                    <label key={reason} className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${dismissReason === reason ? 'bg-destructive/10 border-destructive/30' : 'bg-muted/30 border-border/30 hover:border-destructive/20'}`}>
                      <input 
                        type="radio" 
                        name="dismiss-reason" 
                        checked={dismissReason === reason}
                        onChange={() => setDismissReason(reason)}
                        className="w-4 h-4 text-destructive focus:ring-destructive border-border"
                      />
                      <span className="text-xs font-medium text-foreground">{reason}</span>
                    </label>
                  ))}
                </div>

                <div className="pt-2">
                  <button 
                    disabled={!dismissReason}
                    onClick={() => {
                      if (saved.includes(dismissingOpp.id)) {
                        handleSave(dismissingOpp.id);
                      }
                      const newDismissed = [...dismissedOpps, dismissingOpp.id];
                      setDismissedOpps(newDismissed);
                      localStorage.setItem('dismissed_opps', JSON.stringify(newDismissed));
                      setDismissingOpp(null);
                      setDismissReason("");
                    }}
                    className="w-full py-3.5 rounded-xl bg-destructive text-destructive-foreground text-sm font-bold hover:bg-destructive/90 active:scale-[0.98] transition-all cursor-pointer border-none flex items-center justify-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Hide Opportunity
                  </button>
                </div>
              </div>
           </motion.div>
        </div>
      , document.body)}
    </div>
  );
};

export default OpportunitiesFeed;
